/**
 * One address result in global search, plus its create action (RUT-4447).
 *
 * The row itself still opens the parcel on the map. The trailing action starts
 * a profile at this address: "Create customer here" for an address nobody lives
 * at, "New owner moving in?" when a customer is already on file there (that
 * runs the existing New-owner-at-property flow, RUT-1455).
 */
import { CaretRight, MapPin } from '@phosphor-icons/react';
import { CommandItem } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import type { ExistingCustomerAtAddress } from '@/lib/addressSearchResults';

export interface AddressResult {
  id: string;
  place_name: string;
  center: [number, number];
  streetAddress: string;
  cityStateZip: string;
  existingCustomer?: ExistingCustomerAtAddress;
}

export function addressCreateLabel(address: Pick<AddressResult, 'existingCustomer'>): string {
  return address.existingCustomer ? 'New owner moving in?' : 'Create customer here';
}

export function AddressResultRow({
  address,
  isDark,
  canCreate,
  onOpen,
  onCreate,
}: {
  address: AddressResult;
  isDark?: boolean;
  /** Office users who can add customers (CUSTOMERS_MANAGE). */
  canCreate: boolean;
  onOpen: (address: AddressResult) => void;
  onCreate: (address: AddressResult) => void;
}) {
  return (
    <CommandItem
      value={`address-${address.id}-${address.place_name}`}
      onSelect={() => onOpen(address)}
      className={cn(
        'w-full group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
        'cursor-pointer text-left',
        isDark ? 'data-[selected=true]:bg-white/10' : 'data-[selected=true]:bg-stone-100',
      )}
    >
      <div
        className={cn(
          'shrink-0 w-8 h-8 rounded-lg flex items-center justify-center',
          isDark ? 'bg-white/10' : 'bg-red-50',
        )}
      >
        <MapPin className={cn('w-4 h-4', isDark ? 'text-red-400' : 'text-red-500')} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={cn('text-sm truncate', isDark ? 'text-white' : 'text-stone-900')}>
          {address.streetAddress}
        </div>
        <div className={cn('text-xs truncate', isDark ? 'text-white/50' : 'text-stone-500')}>
          {[address.cityStateZip, address.existingCustomer && `${address.existingCustomer.name} lives here`]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </div>
      {canCreate && (
        <button
          type="button"
          className={cn(
            'shrink-0 rounded-md px-2 py-1 text-xs font-medium transition-colors',
            isDark
              ? 'text-white/80 hover:bg-white/10 hover:text-white'
              : 'text-primary hover:bg-primary/10',
          )}
          // cmdk selects the row on pointer down; keep the action its own click.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCreate(address);
          }}
        >
          {addressCreateLabel(address)}
        </button>
      )}
      <CaretRight
        className={cn(
          'w-4 h-4 shrink-0 transition-transform',
          isDark
            ? 'text-white/30 group-data-[selected=true]:text-white/60'
            : 'text-stone-500 group-data-[selected=true]:text-stone-700',
          'group-data-[selected=true]:translate-x-0.5',
        )}
      />
    </CommandItem>
  );
}
